import dayjs from 'dayjs';
import {ChangeEvent, useState} from 'react';
import styled from 'styled-components';
import {useChangeSubscription} from '@/api/payments';
import {getGqlErrorMessage, isAccessRestrictionError} from '@/api/request';
import {Button, ButtonGroup} from '@/components/Button';
import {Checkbox, CheckboxItem, CheckboxLabel} from '@/components/Checkbox';
import toast from '@/components/HotToast';
import {Modal} from '@/components/Modal';
import {Typography} from '@/components/Typography';
import {FULL_DATE_FORMAT} from '@/constants';
import {BillingStatusType, CancelReason, ProductTier} from '@/enums';
import {useSession} from '@/hooks';
import {useAirbridgeTracking} from '@/hooks/tracking';
import {
  ColumnFlexBox,
  GrayRoundedBox,
  PaddingBox,
  SimpleListBox,
} from '@/styled-components';
import {palette} from '@/styles';
import SadImage from './assets/img-sad.svg';

const REASONS_LABELS = [
  {key: CancelReason.DataIssue, label: '데이터가 정확하지 않습니다.'},
  {key: CancelReason.SwitchedService, label: '다른 서비스를 이용하려 합니다.'},
  {key: CancelReason.NoLongerNeeded, label: '필요한 데이터나 기능이 없습니다.'},
  {key: CancelReason.PriceIssue, label: '요금이 너무 비쌉니다.'},
];

enum SubscribeType {
  benefit = 'benefit',
  default = 'default',
}

const NOTE = {
  [SubscribeType.benefit]: [
    '해지 신청 시 다음 정기 결제일에 결제가 진행되지 않으며, 이용 기간 만료 후 해지 처리됩니다.',
    '구독 해지 처리가 되면 사전 신청 단독 혜택(매장 찾기 기능)을 다시 이용할 수 없습니다.',
    '남은 이용 기간에 대한 금액은 환불되지 않습니다.',
  ],
  [SubscribeType.default]: [
    '해지 신청 시 다음 정기 결제일에 결제가 진행되지 않으며, 이용 기간 만료 후 해지 처리됩니다.',
    '남은 이용 기간에 대한 금액은 환불되지 않습니다.',
  ],
};

interface IProps {
  onClose?(): void;
  onComplete?(record: any): void;
}

export const CancelPlanModal = ({onClose, onComplete}: IProps) => {
  const {user, isOpenPreRegistrationUser} = useSession();
  const billingInfo = user?.billing;
  const [step, setStep] = useState<number>(0);
  const [reason, setReason] = useState<CancelReason>();
  const isTrial = user?.tier === ProductTier.Trial;
  const subscribeType = isOpenPreRegistrationUser
    ? SubscribeType.benefit
    : SubscribeType.default;

  const {mutate, isPending} = useChangeSubscription();
  const trackAirbridge = useAirbridgeTracking();

  const handleReasons = (e: ChangeEvent<HTMLInputElement>) => {
    const key = e.target.value as CancelReason;

    setReason((prev) => (key === prev ? undefined : key));
  };

  const handleClickNext = () => {
    setStep((prev) => prev + 1);
  };

  const cancelPlan = () => {
    if (
      !billingInfo ||
      BillingStatusType.Quit === billingInfo.status ||
      isPending
    ) {
      return;
    }

    const reasonKorean = REASONS_LABELS.find((i) => i.key === reason)?.label;

    mutate(
      {
        reason,
        state: BillingStatusType.Quit,
      },
      {
        onSuccess: (data) => {
          if (data.result) {
            trackAirbridge('airbridge.unsubscribe', {
              action: user.billing?.orderName,
              value: user.billing?.totalAmount,
              semanticAttributes: {
                currency: 'KRW',
              },
              customAttributes: {
                reason: reasonKorean,
              },
            });

            onComplete?.(billingInfo);
          } else {
            toast.error('해지 신청이 실패했습니다. 잠시 후 다시 시도해주세요.');
          }
        },
        onError: (error) => {
          // 이용 제한은 공통 에러 핸들러가 안내 모달을 띄운다.
          if (!isAccessRestrictionError(error)) {
            toast.error(getGqlErrorMessage(error));
          }
        },
      },
    );
  };

  return (
    <Modal title="구독 해지 신청 확인" width={328} onClose={onClose}>
      <PaddingBox spacing={24}>
        {step === 0 ? (
          <div>
            <ColumnFlexBox gap={16}>
              <Typography variant="medium14" color={palette.gray600}>
                마이프차 지도 서비스를 이용하는 데 어떤 점이 불편하셨나요? (택1)
                <Typography color={palette.primary400}>*</Typography>
              </Typography>
              {REASONS_LABELS.map(({key, label}) => {
                return (
                  <CheckboxItem key={key} gap="8px">
                    <Checkbox
                      variant="large"
                      name={key}
                      value={key}
                      checked={key === reason}
                      onChange={handleReasons}
                    />
                    <CheckboxLabel>{label}</CheckboxLabel>
                  </CheckboxItem>
                );
              })}
            </ColumnFlexBox>
            <PaddingBox top={24}>
              <ButtonGroup flex={1} spacing={2}>
                <Button
                  size="medium"
                  color="white"
                  variant="fill"
                  fullWidth
                  disabled={!reason}
                  onClick={handleClickNext}
                >
                  다음
                </Button>
                <Button
                  size="medium"
                  color="secondary"
                  variant="fill"
                  fullWidth
                  onClick={onClose}
                >
                  해지 신청 취소
                </Button>
              </ButtonGroup>
            </PaddingBox>
          </div>
        ) : (
          <div>
            <ColumnFlexBox gap={16}>
              <Typography variant="bold18" align="center">
                {subscribeType === SubscribeType.default ? (
                  <>
                    {billingInfo?.orderName || '이용권'} 구독을 <br />
                    정말 해지하시겠어요?
                  </>
                ) : (
                  <>
                    지금 해지하시면
                    <br />
                    사전 신청 단독 혜택이 사라져요!
                  </>
                )}
              </Typography>
              <Image />
              {subscribeType !== SubscribeType.default && (
                <GrayBox>
                  <Typography variant="medium14">
                    '매장 찾기' 기능을 다시 사용할 수 없어요
                  </Typography>
                  <Typography variant="bold14" color={palette.blue500}>
                    <a
                      href="https://myfranchise.notion.site/10a9a0c9c42a80948d33db68ead487fa?pvs=4"
                      target="_blank"
                      rel="noreferrer"
                    >
                      🗺️ '매장 찾기' 기능이란 →
                    </a>
                  </Typography>
                </GrayBox>
              )}
              <Typography
                variant="medium14"
                color={palette.gray600}
                align="center"
              >
                {dayjs(user?.useEndedAt).format(FULL_DATE_FORMAT)}까지
                {isTrial
                  ? ' 무료로 이용하실 수 있습니다.'
                  : ' 이용하실 수 있습니다.'}
              </Typography>
              <SimpleListBox>
                <ul>
                  {NOTE[subscribeType].map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
              </SimpleListBox>
            </ColumnFlexBox>
            <PaddingBox top={24}>
              <ButtonGroup flex={1} spacing={2}>
                <Button
                  size="medium"
                  color="white"
                  variant="fill"
                  fullWidth
                  disabled={isPending}
                  onClick={cancelPlan}
                >
                  {isPending ? '해지 신청 중...' : '구독 해지'}
                </Button>
                <Button
                  size="medium"
                  color="secondary"
                  variant="fill"
                  fullWidth
                  onClick={onClose}
                >
                  유지하기
                </Button>
              </ButtonGroup>
            </PaddingBox>
          </div>
        )}
      </PaddingBox>
    </Modal>
  );
};

const Image = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 100%;
  margin: auto;
  background-image: url(${SadImage});
  background-size: cover;
`;

const GrayBox = styled(GrayRoundedBox)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;
